import React, { useEffect, useState } from 'react';
import { PageHeader, EmptyState } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Building2, PlusCircle, ArrowRight, Users, ClipboardList, Trash2, ArrowRightLeft, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useAuth } from '@/lib/auth';

function CompanyDialog({ existing, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const initial = () => ({
    name: existing?.name || '',
    industry: existing?.industry || '',
    address: existing?.address || '',
    gst: existing?.gst || '',
    pan: existing?.pan || '',
    directors_str: (existing?.directors || []).map((d) => `${d.name || ''} | ${d.email || ''} | ${d.role || ''}`).join('\n'),
    default_cc_emails_str: (existing?.default_cc_emails || []).join(', '),
    notes: existing?.notes || '',
  });
  const [form, setForm] = useState(initial());
  useEffect(() => { if (open) setForm(initial()); }, [open, existing?.id]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Company name is required');
    const directors = form.directors_str.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
      const [name, email, role] = line.split('|').map((s) => (s || '').trim());
      return { name: name || 'Director', email: email || '', role: role || 'Director' };
    }).filter((d) => d.email);
    const default_cc_emails = form.default_cc_emails_str.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    const payload = {
      name: form.name, industry: form.industry, address: form.address, gst: form.gst, pan: form.pan,
      directors, default_cc_emails, notes: form.notes,
    };
    try {
      if (existing) await api.patch(`/companies/${existing.id}`, payload);
      else await api.post('/companies', payload);
      toast.success(existing ? 'Company updated' : 'Company created');
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const defaultTrigger = existing ? (
    <Button variant="ghost" size="icon" data-testid={`company-edit-${existing.id}`} title="Edit company">
      <Pencil className="h-4 w-4 text-muted-foreground" />
    </Button>
  ) : (
    <Button data-testid="companies-add-button"><PlusCircle className="h-4 w-4 mr-2" />Add company</Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.name}` : 'Add client company'}</DialogTitle>
          <DialogDescription>Company details, directors and default CC list for every outbound email.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-1.5 md:col-span-2"><Label>Company name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" /></div>
          <div className="space-y-1.5"><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Fintech, SaaS, Retail…" data-testid="company-industry-input" /></div>
          <div className="space-y-1.5"><Label>GST</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
          <div className="space-y-1.5 md:col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Directors / Managers</Label>
            <Textarea rows={3} placeholder={'Name | email@example.com | Role\nRavi Kumar | ravi@acme.com | Director\nAnita Rao | anita@acme.com | HR Manager'} value={form.directors_str} onChange={(e) => setForm({ ...form, directors_str: e.target.value })} data-testid="company-directors-input" />
            <p className="text-xs text-muted-foreground">One person per line, separated by <span className="font-mono">|</span></p>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Default CC emails</Label>
            <Textarea rows={2} placeholder={'directors@acme.com, hr-lead@acme.com'} value={form.default_cc_emails_str} onChange={(e) => setForm({ ...form, default_cc_emails_str: e.target.value })} data-testid="company-cc-input" />
            <p className="text-xs text-muted-foreground">Applied to every outbound email for this company.</p>
          </div>
          <div className="space-y-1.5 md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="company-save-button">{existing ? 'Save changes' : 'Create company'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestTransferDialog({ company }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [toUserId, setToUserId] = useState('');
  const [reason, setReason] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    api.get('/transfer-requests/teammates')
      .then((r) => setUsers(r.data || []))
      .catch(() => setUsers([]));
  }, [open]);

  const submit = async () => {
    if (!toUserId) return toast.error('Choose a team member to transfer to');
    try {
      await api.post('/transfer-requests', { company_id: company.id, to_user_id: toUserId, reason });
      toast.success('Transfer request submitted for admin approval');
      setOpen(false);
      setReason(''); setToUserId('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`request-transfer-${company.id}`} title="Request transfer">
          <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request transfer of {company.name}</DialogTitle>
          <DialogDescription>Admin will review and approve or reject. Once approved, this company will move to the selected team member.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Transfer to</Label>
            <Select value={toUserId} onValueChange={setToUserId}>
              <SelectTrigger data-testid="transfer-to-user"><SelectValue placeholder="Choose team member" /></SelectTrigger>
              <SelectContent>
                {users.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No teammates available. Ask admin to add users first.</div>}
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Reason (optional)</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="transfer-reason" placeholder="Context to help the admin decide" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="transfer-submit">Submit request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const load = () => api.get('/companies').then((r) => setCompanies(r.data));
  useEffect(() => { load(); }, []);

  // Legacy URL: /companies?new=1 → clear the param (Add dialog now opens via header button)
  useEffect(() => {
    if (params.get('new') === '1') { params.delete('new'); setParams(params); }
  }, [params]);

  const del = async (id) => {
    try { await api.delete(`/companies/${id}?cascade=true`); toast.success('Company deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <>
      <PageHeader
        title="Client Companies"
        description="All companies where you provide HR consultancy - recruitment, onboarding, policies, training, payroll."
        actions={isAdmin ? <CompanyDialog onSaved={load} /> : null}
      />

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {companies.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No client companies yet"
            description={isAdmin ? "Add your first client company to start managing candidates, onboarding, policies, training and payroll for them." : "You don't have any assigned companies yet. Ask an admin to assign one to you."}
            action={isAdmin ? <CompanyDialog onSaved={load} trigger={<Button data-testid="companies-empty-cta"><PlusCircle className="h-4 w-4 mr-2" />Add company</Button>} /> : null}
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((c) => (
              <Card key={c.id} className="p-5 flex flex-col hover:shadow-sm transition-shadow" data-testid={`company-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-display font-semibold">{c.name[0]}</div>
                    <div className="min-w-0">
                      <p className="font-display font-semibold truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.industry || 'Client company'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {isAdmin && <CompanyDialog existing={c} onSaved={load} />}
                    {isAdmin ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`company-delete-${c.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This will remove the company and all associated candidates, onboardings, policies, training, payroll data. This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(c.id)} data-testid={`company-delete-confirm-${c.id}`}>Delete permanently</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <RequestTransferDialog company={c} />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="rounded-lg bg-muted/60 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> Candidates</div>
                    <p className="font-display text-xl font-semibold">{c.candidate_count || 0}</p>
                  </div>
                  <div className="rounded-lg bg-muted/60 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ClipboardList className="h-3 w-3" /> Onboarding</div>
                    <p className="font-display text-xl font-semibold">{c.onboarding_count || 0}</p>
                  </div>
                </div>
                <Button variant="outline" className="mt-4" onClick={() => navigate(`/companies/${c.id}/recruitment`)} data-testid={`company-open-${c.id}`}>
                  Open workspace <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
